import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { marketplaceApi, reviewApi } from '../utils/api';
import ChessDiagram from '../components/ChessDiagram';

// Parse the Postgres array literal  {"chess","openings","eco"}  into JS strings
function parseTags(raw) {
  if (!raw) return [];
  const inner = raw.replace(/^\{/, '').replace(/\}$/, '');
  if (!inner) return [];
  return inner.split(',').map(t => t.replace(/^"(.*)"$/, '$1').trim()).filter(Boolean);
}

// ── Partial-fill star rating display ─────────────────────────────────────────
// Renders 5 stars where each star can be fully filled, partially filled, or hollow.
// Uses an SVG clip trick so any fractional value (e.g. 2.5) renders precisely.

function PartialStars({ rating, count }) {
  if (!count || count === 0) return null;
  const pct = Math.min(Math.max(rating / 5, 0), 1) * 100;
  const id  = `star-clip-${Math.round(rating * 10)}`;   // stable enough for a list

  return (
    <div className="d-flex align-items-center gap-1" style={{ lineHeight: 1 }}>
      <svg width="90" height="18" viewBox="0 0 90 18" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id={id}>
            <rect x="0" y="0" width={`${pct}%`} height="18" />
          </clipPath>
        </defs>
        {/* Hollow stars (background) */}
        {[0,1,2,3,4].map(i => (
          <text key={i} x={i * 18} y="14" fontSize="16" fill="rgba(255,255,255,0.35)"
                fontFamily="Arial, sans-serif">★</text>
        ))}
        {/* Filled stars clipped to the rating percentage */}
        <g clipPath={`url(#${id})`}>
          {[0,1,2,3,4].map(i => (
            <text key={i} x={i * 18} y="14" fontSize="16" fill="#f5c542"
                  fontFamily="Arial, sans-serif">★</text>
          ))}
        </g>
      </svg>
      <span style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.65)', whiteSpace: 'nowrap' }}>
        {rating.toFixed(1)} ({count})
      </span>
    </div>
  );
}

// ── Reviews modal ────────────────────────────────────────────────────────────

const PAGE_SIZE = 5;

function Stars({ rating }) {
  return (
    <span style={{ color: '#f5a623', letterSpacing: '0.05em' }}>
      {'★'.repeat(rating)}
      <span style={{ color: '#ccc' }}>{'★'.repeat(5 - rating)}</span>
    </span>
  );
}

function ReviewsModal({ offering, token, onClose }) {
  const overlayRef                    = useRef(null);
  const [reviews, setReviews]         = useState([]);
  const [visible, setVisible]         = useState(PAGE_SIZE);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reviewApi.listByMpo(offering.mpo_id, token)
      .then(data  => { if (!cancelled) { setReviews(data || []); setLoading(false); } })
      .catch(err  => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [offering.mpo_id, token]);

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const shownReviews = reviews.slice(0, visible);
  const hasMore      = visible < reviews.length;

  return (
    <div
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1055,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div style={{
        background: 'var(--bs-body-bg)',
        borderRadius: '0.875rem',
        width: '100%',
        maxWidth: 560,
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, var(--theme-brand-nav) 0%, #2d5f8a 100%)',
          padding: '1rem 1.25rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div className="text-white fw-semibold" style={{ fontSize: '1rem' }}>
              Reviews — {offering.title}
            </div>
            {!loading && !error && (
              <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', marginTop: '0.15rem' }}>
                {reviews.length === 0
                  ? 'No reviews yet'
                  : `${reviews.length} review${reviews.length !== 1 ? 's' : ''}`}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: '50%', width: 32, height: 32, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.1rem', lineHeight: 1, flexShrink: 0,
          }} aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '1rem 1.25rem', flexGrow: 1 }}>
          {loading && (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm" style={{ color: 'var(--theme-brand-text)' }} />
            </div>
          )}
          {!loading && error && (
            <div className="alert alert-danger py-2 small">{error}</div>
          )}
          {!loading && !error && reviews.length === 0 && (
            <p className="text-muted text-center my-4">No reviews have been posted for this deck yet.</p>
          )}
          {!loading && !error && shownReviews.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {shownReviews.map(r => (
                <div key={r.id} style={{
                  border: '1px solid var(--bs-border-color)',
                  borderRadius: '0.6rem',
                  padding: '0.75rem 1rem',
                  background: 'var(--bs-tertiary-bg)',
                }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Stars rating={r.mpo_rating} />
                    <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
                      {new Date(r.created_date).toLocaleDateString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric',
                      })}
                    </span>
                  </div>
                  {r.mpo_review && (
                    <p className="mb-0" style={{ fontSize: '0.87rem', color: 'var(--bs-body-color)', whiteSpace: 'pre-wrap' }}>
                      {r.mpo_review}
                    </p>
                  )}
                </div>
              ))}
              {hasMore && (
                <div className="text-center pt-1">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    style={{ borderRadius: '0.5rem', fontSize: '0.82rem' }}
                    onClick={() => setVisible(v => v + PAGE_SIZE)}
                  >
                    Load more ({reviews.length - visible} remaining)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid var(--bs-border-color)',
          padding: '0.75rem 1.25rem',
          flexShrink: 0,
          textAlign: 'right',
        }}>
          <button onClick={onClose} className="btn btn-sm" style={{
            background: 'linear-gradient(135deg, var(--theme-brand-nav), #2d5f8a)',
            color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.35rem 1.1rem',
          }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Preview modal ─────────────────────────────────────────────────────────

function PreviewModal({ offering, onClose, prefs }) {
  const overlayRef = useRef(null);
  const preview = (offering.flashcards || []).slice(0, 5);

  // Close on backdrop click
  const handleOverlayClick = e => {
    if (e.target === overlayRef.current) onClose();
  };

  // Close on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 1050,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div style={{
        background: 'var(--bs-body-bg)',
        borderRadius: '0.875rem',
        width: '100%',
        maxWidth: 580,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Modal header */}
        <div style={{
          background: 'linear-gradient(135deg, var(--theme-brand-nav) 0%, #2d5f8a 100%)',
          padding: '1rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div>
            <div className="text-white fw-semibold" style={{ fontSize: '1rem' }}>
              ♟ {offering.title}
            </div>
            <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', marginTop: '0.15rem' }}>
              Showing {preview.length} of {(offering.flashcards || []).length} cards
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              color: '#fff',
              borderRadius: '50%',
              width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '1.1rem',
              lineHeight: 1,
              flexShrink: 0,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Card list */}
        <div style={{ overflowY: 'auto', padding: '1rem 1.25rem', flexGrow: 1 }}>
          {preview.length === 0 ? (
            <p className="text-muted text-center my-4">No cards in this offering.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {preview.map((fc, idx) => (
                <div key={fc.id} style={{
                  border: '1px solid var(--bs-border-color)',
                  borderRadius: '0.6rem',
                  overflow: 'hidden',
                }}>
                  {/* Card number + name */}
                  <div style={{
                    background: 'var(--bs-tertiary-bg)',
                    borderBottom: '1px solid var(--bs-border-color)',
                    padding: '0.45rem 0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6rem',
                  }}>
                    <span style={{
                      background: 'var(--theme-brand-nav)',
                      color: '#fff',
                      borderRadius: '50%',
                      width: 22, height: 22,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}>
                      {idx + 1}
                    </span>
                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--theme-brand-text)' }}>
                      {fc.name}
                    </span>
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: '0.72rem',
                      color: 'var(--bs-secondary-color)',
                      background: 'var(--bs-secondary-bg)',
                      borderRadius: '999px',
                      padding: '0.1rem 0.55rem',
                    }}>
                      {fc.to_move === 'W' ? 'White to move' : 'Black to move'}
                    </span>
                  </div>

                  {/* Diagram + problem */}
                  <div style={{ padding: '0.6rem 0.85rem', fontSize: '0.82rem', color: 'var(--bs-body-color)' }}>
                    {fc.fen && (
                      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: fc.problem ? '0.6rem' : 0 }}>
                        <ChessDiagram
                          fen={fc.fen}
                          prefs={{ ...prefs, diagramFlipped: fc.flipped }}
                          size={200}
                        />
                      </div>
                    )}
                    {fc.problem && (
                      <div>
                        <span style={{ fontWeight: 600, color: 'var(--theme-brand-text)' }}>Problem: </span>
                        {fc.problem}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid var(--bs-border-color)',
          padding: '0.75rem 1.25rem',
          flexShrink: 0,
          textAlign: 'right',
        }}>
          <button
            onClick={onClose}
            className="btn btn-sm"
            style={{
              background: 'linear-gradient(135deg, var(--theme-brand-nav), #2d5f8a)',
              color: '#fff', border: 'none', borderRadius: '0.5rem',
              padding: '0.35rem 1.1rem',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Purchase confirmation modal ───────────────────────────────────────────

function PurchaseModal({ offering, onConfirm, onClose, purchasing, error }) {
  const overlayRef = useRef(null);
  const priceLabel = (!offering.price || offering.price === 0)
    ? 'Free'
    : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(offering.price);

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1060,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div style={{
        background: 'var(--bs-body-bg)',
        borderRadius: '0.875rem',
        width: '100%',
        maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, var(--theme-brand-nav) 0%, #2d5f8a 100%)',
          padding: '1rem 1.25rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span className="text-white fw-semibold" style={{ fontSize: '1rem' }}>
            Confirm Purchase
          </span>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
            borderRadius: '50%', width: 32, height: 32, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.1rem', lineHeight: 1,
          }} aria-label="Close">×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '1.5rem 1.25rem' }}>
          <p style={{ fontSize: '0.95rem', color: 'var(--bs-body-color)', marginBottom: '0.75rem' }}>
            Are you sure you want to purchase this card deck?
          </p>
          <div style={{
            background: 'var(--bs-tertiary-bg)', border: '1px solid var(--bs-border-color)',
            borderRadius: '0.6rem', padding: '0.75rem 1rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontWeight: 600, color: 'var(--theme-brand-text)', fontSize: '0.9rem' }}>
              {offering.title}
            </span>
            <span style={{
              fontWeight: 700, fontSize: '0.9rem',
              color: (!offering.price || offering.price === 0) ? '#059669' : 'var(--theme-brand-text)',
            }}>
              {priceLabel}
            </span>
          </div>
          {error && (
            <div className="alert alert-danger mt-3 mb-0 py-2" style={{ fontSize: '0.85rem' }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid var(--bs-border-color)', padding: '0.75rem 1.25rem',
          display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
        }}>
          <button onClick={onClose} disabled={purchasing}
            className="btn btn-sm btn-outline-secondary" style={{ borderRadius: '0.5rem', minWidth: 80 }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={purchasing}
            className="btn btn-sm"
            style={{
              background: 'linear-gradient(135deg, var(--theme-brand-nav), #2d5f8a)',
              color: '#fff', border: 'none', borderRadius: '0.5rem', minWidth: 80,
            }}>
            {purchasing ? <><span className="spinner-border spinner-border-sm me-1" />Processing…</> : 'Yes, Purchase'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Offering card ─────────────────────────────────────────────────────────

function OfferingCard({ offering, onPreview, onPurchase, onReviews, purchased }) {
  const tags = parseTags(offering.tags);
  const cardCount = offering.flashcards ? offering.flashcards.length : 0;
  const created = new Date(offering.created_date).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  const priceLabel = purchased
    ? 'Purchased'
    : (!offering.price || offering.price === 0)
      ? 'Free'
      : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(offering.price);
  const isFree = priceLabel === 'Free';
  const isPurchased = purchased;

  return (
    <div className="col">
      <div className="card h-100 shadow-sm border-0" style={{ borderRadius: '0.75rem', overflow: 'hidden' }}>

        {/* ── Colored header band ─────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg, var(--theme-brand-nav) 0%, #2d5f8a 100%)',
          padding: '1.1rem 1.25rem 0.8rem',
        }}>
          <div className="d-flex justify-content-between align-items-start gap-2">
            <h5 className="mb-0 text-white fw-semibold lh-sm" style={{ fontSize: '1rem' }}>
              {offering.title}
            </h5>
            <span style={{
              flexShrink: 0,
              background: isPurchased ? 'rgba(72,199,142,0.25)' : isFree ? 'rgba(72,199,142,0.25)' : 'rgba(255,255,255,0.15)',
              color: isPurchased ? '#6ee7b7' : isFree ? '#6ee7b7' : '#fff',
              border: isPurchased ? '1px solid rgba(72,199,142,0.5)' : isFree ? '1px solid rgba(72,199,142,0.5)' : '1px solid rgba(255,255,255,0.3)',
              borderRadius: '999px',
              fontSize: '0.75rem',
              fontWeight: 600,
              padding: '0.15rem 0.65rem',
              letterSpacing: '0.02em',
            }}>
              {isPurchased ? '✓ Purchased' : priceLabel}
            </span>
          </div>

          {/* Creator + card count + date row */}
          <div className="d-flex align-items-center gap-3 mt-2"
               style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)' }}>
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor"
                   viewBox="0 0 16 16" className="me-1" style={{ verticalAlign: '-1px' }}>
                <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4z"/>
              </svg>
              {offering.creator_nickname || offering.user_id}
            </span>
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor"
                   viewBox="0 0 16 16" className="me-1" style={{ verticalAlign: '-1px' }}>
                <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4.5l-3 3-.5.5V4a2 2 0 0 1 2-2H2z"/>
              </svg>
              {cardCount} {cardCount === 1 ? 'card' : 'cards'}
            </span>
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="currentColor"
                   viewBox="0 0 16 16" className="me-1" style={{ verticalAlign: '-1px' }}>
                <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
              </svg>
              {created}
            </span>
          </div>

          {/* Average rating */}
          <div className="mt-2">
            <PartialStars rating={offering.avg_rating} count={offering.review_count} />
          </div>
        </div>

        {/* ── Card body ────────────────────────────────────────── */}
        <div className="card-body d-flex flex-column p-0">

          {/* Scrollable description */}
          <div style={{
            padding: '0.9rem 1.25rem',
            overflowY: 'auto',
            flexGrow: 1,
            maxHeight: '120px',
            fontSize: '0.875rem',
            color: 'var(--bs-body-color)',
            lineHeight: '1.55',
            borderBottom: tags.length ? '1px solid var(--bs-border-color)' : 'none',
          }}>
            {offering.description
              ? offering.description
              : <span className="text-muted fst-italic">No description provided.</span>}
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div style={{ padding: '0.6rem 1.25rem 0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
              {tags.map(tag => (
                <span key={tag} style={{
                  background: 'var(--bs-tertiary-bg)', color: 'var(--theme-brand-text)', border: '1px solid var(--bs-border-color)',
                  borderRadius: '999px', fontSize: '0.72rem', padding: '0.15rem 0.65rem', fontWeight: 500,
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer actions ───────────────────────────────────── */}
        <div className="card-footer bg-transparent border-top-0 px-3 pb-3 pt-0 d-flex gap-2">
          <button
            className="btn btn-sm"
            onClick={() => onPreview(offering)}
            style={{
              background: 'transparent',
              color: 'var(--theme-brand-text)',
              border: '1px solid var(--bs-border-color)',
              borderRadius: '0.5rem',
              fontSize: '0.78rem',
              fontWeight: 500,
              flex: 1,
            }}
          >
            ♟ Preview
          </button>
          <button
            className="btn btn-sm"
            onClick={() => onReviews(offering)}
            style={{
              background: 'transparent',
              color: 'var(--theme-brand-text)',
              border: '1px solid var(--bs-border-color)',
              borderRadius: '0.5rem',
              fontSize: '0.78rem',
              fontWeight: 500,
              flex: 1,
            }}
          >
            ★ Reviews
          </button>
          {!isPurchased && (
            <button
              className="btn btn-sm"
              onClick={() => onPurchase(offering)}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, var(--theme-brand-nav), #2d5f8a)',
                color: '#fff',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.78rem',
                fontWeight: 500,
              }}
            >
              Purchase
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function MarketplacePage() {
  const { token, prefs } = useAuth();
  const [offerings, setOfferings]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [search, setSearch]             = useState('');
  const [purchasedIds, setPurchasedIds]     = useState(new Set());
  const [previewOffering, setPreview]       = useState(null);
  const [reviewsOffering, setReviewsOffering] = useState(null);
  const [purchaseOffering, setPurchase]     = useState(null);
  const [purchasing, setPurchasing]         = useState(false);
  const [purchaseError, setPurchaseError]   = useState('');

  const confirmPurchase = useCallback(async () => {
    if (!purchaseOffering) return;
    setPurchasing(true);
    setPurchaseError('');
    try {
      await marketplaceApi.purchase(purchaseOffering.mpo_id, token);
      setPurchasedIds(prev => new Set([...prev, purchaseOffering.mpo_id]));
      setPurchase(null);
    } catch (err) {
      setPurchaseError(err.message);
    } finally {
      setPurchasing(false);
    }
  }, [purchaseOffering, token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, purchases] = await Promise.all([
        marketplaceApi.list(token),
        marketplaceApi.myPurchases(token),
      ]);
      setOfferings(data || []);
      setPurchasedIds(new Set((purchases || []).map(p => p.mpo_id)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const filtered = offerings.filter(o => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.title.toLowerCase().includes(q) ||
      (o.description || '').toLowerCase().includes(q) ||
      (o.creator_nickname || '').toLowerCase().includes(q) ||
      (o.tags || '').toLowerCase().includes(q)
    );
  });

  return (
    <>
      {/* ── Page header ──────────────────────────────────────────── */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 gap-3">
        <div>
          <h2 className="mb-0 fw-bold" style={{ color: 'var(--theme-brand-text)' }}>♟ Marketplace</h2>
          <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
            Explore flashcard sets shared by the community
          </p>
        </div>
        {!loading && !error && (
          <span className="badge rounded-pill" style={{
            background: 'var(--bs-tertiary-bg)', color: 'var(--theme-brand-text)', border: '1px solid var(--bs-border-color)',
            fontSize: '0.8rem', padding: '0.4rem 0.85rem', fontWeight: 500,
          }}>
            {offerings.length} {offerings.length === 1 ? 'offering' : 'offerings'}
          </span>
        )}
      </div>

      {/* ── Search bar ───────────────────────────────────────────── */}
      {!loading && !error && offerings.length > 0 && (
        <div className="mb-4" style={{ maxWidth: 420 }}>
          <div className="input-group">
            <span className="input-group-text bg-white border-end-0" style={{ color: 'var(--bs-secondary-color)' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.099zm-5.242 1.156a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z"/>
              </svg>
            </span>
            <input
              type="text"
              className="form-control border-start-0 ps-0"
              placeholder="Search by title, creator, or tag…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ boxShadow: 'none' }}
            />
          </div>
        </div>
      )}

      {/* ── States ───────────────────────────────────────────────── */}
      {loading && (
        <div className="text-center mt-5 pt-4">
          <div className="spinner-border" style={{ color: 'var(--theme-brand-text)', width: '2.5rem', height: '2.5rem' }} />
          <p className="text-muted mt-3">Loading marketplace…</p>
        </div>
      )}
      {!loading && error && <div className="alert alert-danger">{error}</div>}
      {!loading && !error && offerings.length === 0 && (
        <div className="text-center mt-5 pt-3">
          <div style={{ fontSize: '3rem' }}>♟</div>
          <h5 className="text-muted mt-2">No offerings yet</h5>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Be the first to share a flashcard set with the community.
          </p>
        </div>
      )}
      {!loading && !error && offerings.length > 0 && filtered.length === 0 && (
        <div className="text-center mt-4">
          <p className="text-muted">No offerings match <strong>{search}</strong>.</p>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => setSearch('')}>
            Clear search
          </button>
        </div>
      )}

      {/* ── Card grid ────────────────────────────────────────────── */}
      {!loading && !error && filtered.length > 0 && (
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4">
          {filtered.map(o => (
            <OfferingCard
              key={o.mpo_id}
              offering={o}
              purchased={purchasedIds.has(o.mpo_id)}
              onPreview={setPreview}
              onReviews={setReviewsOffering}
              onPurchase={o => { setPurchaseError(''); setPurchase(o); }}
            />
          ))}
        </div>
      )}

      {/* ── Preview modal ─────────────────────────────────────────── */}
      {previewOffering && (
        <PreviewModal offering={previewOffering} onClose={() => setPreview(null)} prefs={prefs} />
      )}

      {/* ── Reviews modal ─────────────────────────────────────────── */}
      {reviewsOffering && (
        <ReviewsModal offering={reviewsOffering} token={token} onClose={() => setReviewsOffering(null)} />
      )}
      {purchaseOffering && (
        <PurchaseModal
          offering={purchaseOffering}
          onConfirm={confirmPurchase}
          onClose={() => { setPurchase(null); setPurchaseError(''); }}
          purchasing={purchasing}
          error={purchaseError}
        />
      )}
    </>
  );
}
